import type {
  BillNotification,
  PublicBill,
  PushSubscriptionInput,
} from "@boo/shared";

// Friends are not logged in, so this deliberately bypasses apiClient: its 401
// handling would bounce them to /login.
export const PUBLIC_API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/** The link was disabled or expired. */
export class BillClosedError extends Error {
  constructor() {
    super("This bill is closed");
    this.name = "BillClosedError";
  }
}

async function call<T>(
  token: string,
  path: string,
  init?: { method: string; body: unknown },
): Promise<T> {
  const isForm = init?.body instanceof FormData;
  const res = await fetch(`${PUBLIC_API_BASE}/public/bills/${token}${path}`, {
    method: init?.method ?? "GET",
    // A FormData body sets its own multipart boundary header.
    headers:
      init && !isForm ? { "Content-Type": "application/json" } : undefined,
    body: init
      ? isForm
        ? (init.body as FormData)
        : JSON.stringify(init.body)
      : undefined,
  });
  if (res.status === 404) throw new BillClosedError();
  if (!res.ok) {
    let body: { message?: string } | null = null;
    try {
      body = (await res.json()) as { message?: string };
    } catch {
      // non-JSON error body
    }
    throw new Error(body?.message ?? `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const getPublicBill = (token: string) => call<PublicBill>(token, "");

export const setPublicClaim = (
  token: string,
  personId: string,
  itemId: string,
  claimed: boolean,
) =>
  call<PublicBill>(token, "/claims", {
    method: "PUT",
    body: { personId, itemId, claimed },
  });

export const flagPublicItem = (
  token: string,
  personId: string,
  itemId: string,
  note: string,
) =>
  call<PublicBill>(token, "/flags", {
    method: "POST",
    body: { personId, itemId, note },
  });

export function submitPublicSlip(token: string, personId: string, file: File) {
  const form = new FormData();
  form.append("personId", personId);
  form.append("file", file);
  return call<PublicBill>(token, "/slips", { method: "POST", body: form });
}

export const reportPublicCash = (token: string, personId: string) =>
  call<PublicBill>(token, "/cash", { method: "POST", body: { personId } });

export const publicQrUrl = (token: string) =>
  `${PUBLIC_API_BASE}/public/bills/${token}/qr`;

/** `version` busts the cache when the owner changes the photo. */
export const publicAvatarUrl = (
  token: string,
  personId: string,
  version: number,
) =>
  `${PUBLIC_API_BASE}/public/bills/${token}/people/${personId}/avatar?v=${version}`;

export const publicReceiptUrl = (token: string) =>
  `${PUBLIC_API_BASE}/public/bills/${token}/receipt`;

export const confirmPublicShare = (token: string, personId: string) =>
  call<PublicBill>(token, "/review", { method: "POST", body: { personId } });

export const undoPublicShare = (token: string, personId: string) =>
  call<PublicBill>(token, "/review", { method: "DELETE", body: { personId } });

export type PublicNotification = BillNotification & { text: string };

export const getPublicNotifications = (token: string, personId: string) =>
  call<{ unread: number; items: PublicNotification[] }>(
    token,
    `/notifications?personId=${encodeURIComponent(personId)}`,
  );

export const markPublicNotificationsRead = (token: string, personId: string) =>
  call<void>(token, "/notifications/read", {
    method: "POST",
    body: { personId },
  });

export const subscribePublicPush = (
  token: string,
  personId: string,
  sub: PushSubscriptionInput,
) => call<void>(token, "/push", { method: "POST", body: { personId, ...sub } });

export const unsubscribePublicPush = (
  token: string,
  personId: string,
  endpoint: string,
) =>
  call<void>(token, "/push", {
    method: "DELETE",
    body: { personId, endpoint },
  });

export async function fetchPublicVapidKey(): Promise<string | null> {
  const res = await fetch(`${PUBLIC_API_BASE}/push/vapid-key`);
  if (!res.ok) return null;
  return ((await res.json()) as { publicKey: string | null }).publicKey;
}
